import React, { useCallback } from 'react';
import { Firestore } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, CheckCircle, XCircle, Clock, Database, Download, Home, Image, Video, Eraser } from 'lucide-react';
import { useBulkTreatment } from '../../hooks/useBulkTreatments';

import { downloadBulkTreatment, getBQTablePreview } from 'services/apiService'

interface TreatmentDetailProps {
  db: Firestore;
  uid: string;
}

const TreatmentDetail: React.FC<TreatmentDetailProps> = ({ db, uid: _uid }) => {
  const navigate = useNavigate();
  const { project_id, treatment_id } = useParams<{ project_id: string; treatment_id: string }>();
  const { treatment, loading, error } = useBulkTreatment(db, project_id, treatment_id);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [previewRows, setPreviewRows] = React.useState<any[]>([]);
  const [loadingPreview, setLoadingPreview] = React.useState(false);

  const fetchPreview = useCallback(async () => {
    if (!treatment?.results_table || !project_id) return;
    
    setLoadingPreview(true);
    try {
      const [datasetId, tableName] = treatment.results_table.split('.');
      const response = await getBQTablePreview(
        process.env.REACT_APP_PROJECT_ID || 'arcane-ai',
        datasetId,
        tableName,
        ['original_image_url', 'output_url'],
        [], 
        project_id
      );
      setPreviewRows(response.rows);
    } catch (err) {
      console.error('Error fetching preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  }, [project_id, treatment?.results_table]);

  React.useEffect(() => {
    if (treatment?.status === 'completed' && treatment.results_table) {
      fetchPreview();
    }
  }, [fetchPreview, treatment?.results_table, treatment?.status]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'processing':
        return <Loader className="w-6 h-6 text-blue-400 animate-spin" />;
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-400" />;
      case 'cancelled':
        return <XCircle className="w-6 h-6 text-gray-400" />;
      default:
        return <Clock className="w-6 h-6 text-yellow-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'processing':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'failed':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'cancelled':
        return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
      default:
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    }
  };

  const getMediaTypeIcon = (mediaType: 'image' | 'video') => {
    switch (mediaType) {
      case 'video':
        return <Video className="w-5 h-5" />;
      case 'image':
      default:
        return <Image className="w-5 h-5" />;
    }
  };

  const getMediaTypeColor = (mediaType: 'image' | 'video') => {
    switch (mediaType) {
      case 'video':
        return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'image':
      default:
        return 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const calculateProgress = (processed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((processed / total) * 100);
  };

  const handleDownloadResults = async () => {
    if (!treatment?.results_table) return;

    if (!project_id || !treatment_id) return;
    
    setIsDownloading(true);
    try {
      const url = await downloadBulkTreatment(treatment_id, project_id);
      const a = document.createElement('a');
      a.href = url.download_url;
      a.download = url.filename || 'results.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading results:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-gray-100 flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          <Loader className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !treatment) {
    return (
      <div className="text-gray-100 flex flex-col">
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Treatment Not Found</h2>
            <p className="text-gray-400 mb-6">{error || 'The requested treatment does not exist'}</p>
            <button
              onClick={() => navigate(`/projects/${project_id}/bulk-treatments`)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Back to Treatments
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progress = calculateProgress(treatment.processed_rows, treatment.total_rows);

  return (
    <div className="text-gray-100 flex flex-col">
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        {/* Navigation Buttons */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <Home className="w-4 h-4" />
            Start Screen
          </button>
          <span className="text-gray-600">/</span>
          <button
            onClick={() => navigate(`/projects/${project_id}/bulk-treatments`)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Treatments
          </button>
        </div>

        {/* Header */}
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              {getStatusIcon(treatment.status)}
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">{treatment.name}</h1>
                <p className="text-sm text-gray-400 font-mono">
                  {treatment.bigquery_config.project_id}.{treatment.bigquery_config.dataset_id}.{treatment.bigquery_config.table_name}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${getMediaTypeColor(treatment.media_type)}`}>
                {getMediaTypeIcon(treatment.media_type)}
                {treatment.media_type.toUpperCase()}
              </span>
              <span className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(treatment.status)}`}>
                {treatment.status.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          {treatment.status === 'processing' && (
            <div>
              <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                <span className="font-medium">Processing Progress</span>
                <span className="font-mono">{treatment.processed_rows.toLocaleString()} / {treatment.total_rows.toLocaleString()} rows</span>
              </div>
              <div className="w-full bg-gray-700/50 rounded-full h-3 overflow-hidden mb-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="text-center text-lg font-bold text-blue-400">{progress}%</div>
            </div>
          )}

          {/* Completed Stats */}
          {treatment.status === 'completed' && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <div className="text-green-300 font-medium">Processing Complete</div>
                  <div className="text-sm text-gray-400">Successfully processed {treatment.total_rows.toLocaleString()} rows</div>
                </div>
              </div>
              {treatment.results_table && (
                <button
                  onClick={handleDownloadResults}
                  disabled={isDownloading}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Download Results
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Error Message */}
          {treatment.status === 'failed' && treatment.error_message && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <div>
                  <div className="text-red-300 font-medium mb-1">Processing Failed</div>
                  <div className="text-sm text-red-400">{treatment.error_message}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Configuration */}
          <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              Configuration
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Created:</span>
                <span className="text-white">{formatDate(treatment.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Last Updated:</span>
                <span className="text-white">{formatDate(treatment.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Total Rows:</span>
                <span className="text-white font-medium">{treatment.total_rows.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Selected Columns:</span>
                <span className="text-white">{treatment.bigquery_config.selected_columns.length}</span>
              </div>
              {treatment.media_type === 'image' && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Background Removal:</span>
                  {treatment.remove_background ? (
                    <span className="text-emerald-400 flex items-center gap-1.5">
                      <Eraser className="w-3.5 h-3.5" />
                      Enabled
                    </span>
                  ) : (
                    <span className="text-gray-500">Disabled</span>
                  )}
                </div>
              )}
              {treatment.results_table && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Results Table:</span>
                  <span className="text-green-400 font-mono text-xs">{treatment.results_table}</span>
                </div>
              )}
            </div>
          </div>

          {/* Selected Columns */}
          <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Selected Columns</h2>
            <div className="flex flex-wrap gap-2">
              {treatment.bigquery_config.selected_columns.map((col) => (
                <span
                  key={col}
                  className="px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-lg text-sm font-mono text-blue-300"
                >
                  {col}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Results Preview */}
        {treatment.status === 'completed' && (
          <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-400" />
              Results Preview
            </h2>
            
            {loadingPreview ? (
              <div className="flex justify-center p-8">
                <Loader className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : previewRows.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {previewRows.map((row, index) => (
                  <div key={index} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Original</div>
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-800">
                          {row.original_image_url ? (
                            <img 
                              src={row.original_image_url} 
                              alt="Original" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Error';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                              Not available
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Generated</div>
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-800">
                          {treatment.media_type === 'video' ? (
                             <video 
                               src={row.output_url} 
                               controls 
                               className="w-full h-full object-cover"
                             />
                          ) : (
                            <img 
                              src={row.output_url} 
                              alt="Generated" 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://placehold.co/400x400?text=Error';
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-center p-4">
                No preview available
              </div>
            )}
          </div>
        )}

        {/* Prompt Rules or Pattern Rules */}
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            {treatment.treatment_mode === 'pattern' ? 'Pattern Rules' : 'Prompt Rules'}
          </h2>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
              {treatment.treatment_mode === 'pattern' 
                ? JSON.stringify(treatment.pattern_rules, null, 2)
                : JSON.stringify(treatment.prompt_rules, null, 2)
              }
            </pre>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TreatmentDetail;
